import { createSlice, current } from '@reduxjs/toolkit'

import { isCoolingOffPayment } from '../lib/payment-cooling-off/cooling-off'

import {
	cancelReceivingEdit,
	clearCourierDetails,
	clearDelivery,
	commitReceiving,
	companyTransferBonusSubmittedEvent,
	customerIndividualDetailsSubmitedEvent,
	customerLegalDetailsSubmitedEvent,
	deliveryDetailsSubmitedEvent,
	deliveryServiceDeclinedEvent,
	errorEvent,
	filledDeliveryAddressEvent,
	ignorePaymentFromUrl,
	openPvz,
	paymentMethodSubmitedEvent,
	pickupPointDetailsSubmitedEvent,
	pvzDeliverySelectEvent,
	pvzPointDetailsSubmitedEvent,
	selectDelivery,
	selectDeliveryDate,
	setGiftCardEvent,
	setGiftsEvent,
	updatePaymentMethods,
	writeOffBonusesCountSubmitEvent
} from './events'
import { OrderSchema } from './order-schema'

const clearReceivingSelection = (state: OrderSchema) => {
	state.delivery.selectedDelivery = null
	state.recivingOptionPickup = { isSelected: false, shop: null }
	state.recivingOptionDelivery = { isSelected: false, details: null }
	state.recivingOptionPvz = { isSelected: false, details: null }

	state.queryParams.DATE = null

	state.customData.deliveryPrice = null
	state.customData.isDeliveryServiceDeclined = false
}

export const orderInitialState: OrderSchema = {
	queryParams: {
		USER_PAYMENT_BONUS: null,
		DATE: null
	},
	deliveryRequest: null,
	appliedReceiving: null,
	ignorePaymentFromUrl: false,
	customerIndividualDetails: null,
	customerLegalDetails: null,
	recivingOptionPickup: {
		isSelected: false,
		shop: null
	},
	recivingOptionDelivery: {
		isSelected: false,
		details: null
	},
	recivingOptionPvz: {
		isSelected: false,
		details: null
	},
	delivery: {
		selectedDelivery: null
	},
	payments: {},
	paymentMethodDetails: {
		details: null,
		installmentCode: null
	},
	customData: {},
	writeOffBonuses: 0,
	companyTransferBonus: `N`,
	gifts: [],
	error: null,
	giftCard: null
}

export const orderSlice = createSlice({
	name: 'entities/order',
	initialState: orderInitialState,
	reducers: {},
	extraReducers: builder => {
		builder.addCase(updatePaymentMethods, (state, action) => {
			state.payments = action.payload.possiblePayments
		})
		builder.addCase(ignorePaymentFromUrl, state => {
			state.ignorePaymentFromUrl = true
		})
		builder.addCase(setGiftCardEvent, (state, action: { payload: OrderSchema['giftCard'] }) => {
			state.giftCard = {
				...state.giftCard,
				...action.payload
			}
		})
		builder.addCase(customerIndividualDetailsSubmitedEvent, (state, action) => {
			state.customerIndividualDetails = action.payload.details
		})
		builder.addCase(customerLegalDetailsSubmitedEvent, (state, action) => {
			state.customerLegalDetails = action.payload.details
		})
		builder.addCase(companyTransferBonusSubmittedEvent, (state, action) => {
			state.companyTransferBonus = action.payload.value
		})
		builder.addCase(pvzPointDetailsSubmitedEvent, (state, action) => {
			const { details, deliveryPrice } = action.payload

			state.recivingOptionPvz = {
				isSelected: true,
				details: {
					deliveryPvzCode: details.DELIVERY_PVZ_CODE,
					deliveryPvzPartner: details.DELIVERY_PVZ_PARTNER,
					deliveryFiasId: details.DELIVERY_FIAS_ID,
					address: details.FULL_ADDRESS,
					deliveryDate: details?.DELIVERY_DATE
				}
			}

			state.customData = {
				deliveryPrice: deliveryPrice
			}
		})
		builder.addCase(pickupPointDetailsSubmitedEvent, (state, action) => {
			const { shop } = action.payload

			state.recivingOptionPickup = {
				isSelected: Boolean(shop?.STORE_ID),
				shop: shop
			}

			if (shop?.STORE_ID) {
				state.deliveryRequest = { type: 'pickup', storeId: shop.STORE_ID }
			}
		})
		builder.addCase(deliveryDetailsSubmitedEvent, (state, action) => {
			const { details } = action.payload

			// select the current reciving method and update its data
			state.recivingOptionDelivery = {
				details,
				isSelected: true
			}

			// доставку подтвердили заново — прошлый отказ от платной услуги больше не актуален
			state.customData.isDeliveryServiceDeclined = false
		})
		builder.addCase(deliveryServiceDeclinedEvent, state => {
			state.customData.isDeliveryServiceDeclined = true
		})
		builder.addCase(filledDeliveryAddressEvent, (state, action) => {
			const { coords } = action.payload
			const [lat, lon] = coords

			state.deliveryRequest = {
				type: 'courier',
				lat: lat.toString(),
				lon: lon.toString(),
				date: state.deliveryRequest?.type === 'courier' ? state.deliveryRequest.date : state.queryParams.DATE
			}
		})
		builder.addCase(selectDelivery, (state, action) => {
			state.delivery.selectedDelivery = action.payload.delivery
		})
		builder.addCase(selectDeliveryDate, (state, action) => {
			state.queryParams.DATE = action.payload.date

			if (state.deliveryRequest?.type === 'courier') {
				state.deliveryRequest = { ...state.deliveryRequest, date: action.payload.date }
			}
		})
		builder.addCase(clearDelivery, state => {
			clearReceivingSelection(state)
			state.deliveryRequest = { type: 'catalog' }
		})
		builder.addCase(clearCourierDetails, state => {
			const hasCourierSelection = state.recivingOptionDelivery.isSelected || state.recivingOptionDelivery.details
			const hasCourierRequest = state.deliveryRequest?.type === 'courier'
			if (!hasCourierSelection && !hasCourierRequest) return

			state.recivingOptionDelivery = { isSelected: false, details: null }
			if (hasCourierRequest) {
				state.deliveryRequest = { type: 'catalog' }
			}
		})
		builder.addCase(openPvz, (state, action) => {
			clearReceivingSelection(state)
			state.deliveryRequest = { type: 'pvz', fiasId: action.payload.fiasId }
		})
		builder.addCase(cancelReceivingEdit, state => {
			const applied = state.appliedReceiving
			if (!applied) {
				clearReceivingSelection(state)
				state.deliveryRequest = { type: 'catalog' }
				return
			}

			state.deliveryRequest = applied.deliveryRequest
			state.recivingOptionPickup = applied.pickup
			state.recivingOptionDelivery = applied.courier
			state.recivingOptionPvz = applied.pvz
			state.queryParams.DATE = applied.date
			state.customData = applied.customData
			state.delivery.selectedDelivery = applied.selectedDelivery
		})
		builder.addCase(commitReceiving, (state, action) => {
			const snapshot = current(state)
			state.appliedReceiving = {
				deliveryRequest: snapshot.deliveryRequest,
				pickup: snapshot.recivingOptionPickup,
				courier: snapshot.recivingOptionDelivery,
				pvz: snapshot.recivingOptionPvz,
				date: snapshot.queryParams.DATE,
				customData: snapshot.customData,
				selectedDelivery: snapshot.delivery.selectedDelivery,
				orderDelivery: action.payload.orderDelivery
			}
		})
		builder.addCase(paymentMethodSubmitedEvent, (state, action) => {
			state.paymentMethodDetails.details = action.payload.option
			state.paymentMethodDetails.installmentCode = action.payload.installmentCode

			// отказ от платной доставки действует только вместе с кредитным способом оплаты
			if (!isCoolingOffPayment(action.payload.option.CODE)) {
				state.customData.isDeliveryServiceDeclined = false
			}
		})

		builder.addCase(writeOffBonusesCountSubmitEvent, (state, action) => {
			state.writeOffBonuses = action.payload.count
			state.queryParams.USER_PAYMENT_BONUS = action.payload.count
		})
		builder.addCase(pvzDeliverySelectEvent, (state, action) => {
			state.deliveryRequest = { type: 'pvz', fiasId: action.payload.fiasId }
			state.recivingOptionPvz = { isSelected: false, details: null }
			state.customData.deliveryPrice = null
		})
		builder.addCase(setGiftsEvent, (state, action) => {
			state.gifts = action.payload.gifts
		})
		builder.addCase(errorEvent, (state, action) => {
			state.error = action.payload.error
		})
	}
})

export const { actions: orderActions } = orderSlice
export const { reducer: orderReducer } = orderSlice
